import { Shield, CheckCircle2, Calendar, FileText, Award, Lock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import MethodologyPDF from "./MethodologyPDF";

const EnhancedVerification = () => {
  const methodologyContent = `
**Walking Weight Loss Calculator - Methodology v1.0**

**Review Date:** November 22, 2025

**Formulas Used:**

1. **ACSM Walking Equation**
   - VO2 (ml/kg/min) = 0.1 × speed (m/min) + 1.8 × speed × grade + 3.5
   - Most accurate for walking at 3.0-6.5 km/h
   - Citation: ACSM's Guidelines for Exercise Testing and Prescription (2021)

2. **MET Compendium Method**
   - Calories = MET × weight (kg) × duration (hours)
   - Based on Ainsworth BE et al. 2011 Compendium of Physical Activities
   - Validated across diverse populations

3. **Mifflin-St Jeor Equation**
   - BMR = 10 × weight(kg) + 6.25 × height(cm) - 5 × age(y) + s
   - s = +5 for males, -161 for females
   - Citation: Mifflin et al. 1990, American Journal of Clinical Nutrition

4. **Katch-McArdle Formula** (when body fat % available)
   - BMR = 370 + (21.6 × lean mass kg)
   - More accurate when body composition is known

**Assumptions:**
- 1 kg fat ≈ 7,700 kcal deficit
- Step estimation: ~1,300 steps/km
- Terrain modifiers: flat (1.0x), moderate (1.2x), hilly (1.5x)

**Quality Assurance:**
- Cross-validated against NHS, NIDDK, and PrecisionNutrition calculators
- Tested across age ranges 18-75, BMI 18-45
- Confidence intervals account for individual variability

**Limitations:**
- Estimates based on population averages
- Individual metabolism varies ±10-20%
- Does not account for adaptive thermogenesis
- Medical conditions may affect accuracy

**Recommendations:**
Consult healthcare professionals before starting any weight loss program.
  `;

  return (
    <Card className="relative overflow-hidden border-primary/20 bg-gradient-to-br from-card via-card to-primary/5">
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-accent/5 rounded-full blur-3xl" />
      
      <div className="relative p-8">
        {/* Header Badge */}
        <div className="flex items-center justify-center mb-6">
          <Badge variant="default" className="gap-2 px-4 py-2 text-sm font-semibold">
            <Shield className="h-4 w-4" />
            Verified by Datalevo Team
          </Badge>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {/* Creator & Reviewer Section */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Creator */}
            <div className="flex items-start gap-3 p-4 rounded-lg bg-background/50 backdrop-blur-sm border border-border/50">
              <Avatar className="h-12 w-12 border-2 border-primary shadow-md overflow-hidden">
                <img 
                  src="/assets/images/reviewers/shafi-tareen.png" 
                  alt="Shafi Tareen"
                  className="h-full w-full object-cover"
                />
                <AvatarFallback className="bg-primary text-primary-foreground text-base font-semibold">
                  ST
                </AvatarFallback>
              </Avatar>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-sm">Shafi Tareen</p>
                  <Badge variant="secondary" className="text-xs">Creator</Badge>
                </div>
                <p className="text-xs text-muted-foreground">Founder, Datalevo</p>
              </div>
            </div>

            {/* Reviewer */}
            <div className="flex items-start gap-3 p-4 rounded-lg bg-background/50 backdrop-blur-sm border border-border/50">
              <div className="relative">
                <Avatar className="h-12 w-12 border-2 border-accent shadow-md">
                  <AvatarFallback className="bg-accent text-accent-foreground text-base font-semibold">
                    NK
                  </AvatarFallback>
                </Avatar>
                <div className="absolute -bottom-1 -right-1 bg-primary rounded-full p-1">
                  <CheckCircle2 className="h-3 w-3 text-primary-foreground" />
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-sm">Nasir Khan</p>
                  <Badge variant="secondary" className="text-xs">Reviewer</Badge>
                </div>
                <p className="text-xs text-muted-foreground">M.Sc., Exercise Physiology</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Verification Details */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">Review Date</p>
                <p className="font-medium">Nov 22, 2025</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Award className="h-4 w-4 text-accent" />
              <div>
                <p className="text-xs text-muted-foreground">Version</p>
                <p className="font-medium">1.0.0</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm col-span-2 md:col-span-1">
              <Lock className="h-4 w-4 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">Privacy</p>
                <p className="font-medium">Client-First</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Description */}
          <div className="space-y-3">
            <p className="text-sm leading-relaxed text-muted-foreground">
              This tool was created by <span className="font-semibold text-foreground">Shafi Tareen</span> and reviewed by{" "}
              <span className="font-semibold text-foreground">Nasir Khan</span>. The methodology and user experience have been verified for accuracy.
            </p>
            <div className="flex items-start gap-2 p-3 rounded-md bg-primary/5 border border-primary/10">
              <Lock className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <p className="text-xs text-muted-foreground leading-relaxed">
                <span className="font-semibold text-foreground">Privacy First:</span> All calculations are performed locally in your browser. 
                No data is sent to external servers unless you explicitly choose to save or share your results.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" className="flex-1 gap-2 shadow-sm hover:shadow-md transition-shadow">
                  <FileText className="h-4 w-4" />
                  View Complete Methodology
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-xl">
                    <Award className="h-5 w-5 text-primary" />
                    Scientific Methodology & Citations
                  </DialogTitle>
                  <DialogDescription>
                    Comprehensive overview of formulas, assumptions, and research backing this calculator
                  </DialogDescription>
                </DialogHeader>
                <div className="prose prose-sm max-w-none mt-4">
                  <div className="whitespace-pre-wrap text-sm text-foreground">
                    {methodologyContent}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            <MethodologyPDF />
          </div>
        </div>
      </div>
    </Card>
  );
};

export default EnhancedVerification;